/**
 * LiveKit → Aura Field bridge.
 *
 * Writes a 0..1 amplitude into `levelRef` every animation frame without
 * React state. A negative value means "no live audio" (procedural fallback).
 */

'use client'

import type { RefObject } from 'react'

import type { OrbState } from './orb-state'

export type LiveKitAgentState =
  | 'disconnected'
  | 'connecting'
  | 'initializing'
  | 'listening'
  | 'thinking'
  | 'speaking'
  | string

export type LiveKitTrackLike =
  | MediaStreamTrack
  | MediaStream
  | { mediaStreamTrack?: MediaStreamTrack | null }
  | null
  | undefined

export interface LiveKitLevelAdapter {
  levelRef: RefObject<number>
  setTrack: (track: LiveKitTrackLike) => void
  dispose: () => void
}

const VOICE_MIN_HZ = 85
const VOICE_MAX_HZ = 3800
const LEVEL_FLOOR = 0.12
const LEVEL_RANGE = 0.58
const PEAK_WEIGHT = 0.35
const SMOOTHING = 0.18

function mediaTrackOf(track: LiveKitTrackLike): MediaStreamTrack | null {
  if (!track) return null
  if (typeof MediaStream !== 'undefined' && track instanceof MediaStream) {
    return track.getAudioTracks()[0] ?? null
  }
  if (typeof MediaStreamTrack !== 'undefined' && track instanceof MediaStreamTrack) {
    return track.kind === 'audio' ? track : null
  }
  if (typeof track === 'object' && 'mediaStreamTrack' in track) {
    const t = track.mediaStreamTrack
    return t && t.kind === 'audio' ? t : null
  }
  return null
}

/** Map LiveKit voice-assistant agent state → OrbState. */
export function mapAgentState(state: LiveKitAgentState | undefined): OrbState {
  switch (state) {
    case 'connecting':
    case 'initializing':
      return 'connecting'
    case 'listening':
      return 'listening'
    case 'thinking':
      return 'thinking'
    case 'speaking':
      return 'speaking'
    case 'disconnected':
      return 'idle'
    default:
      return 'idle'
  }
}

/**
 * Analyse a LiveKit (or any) audio track into `levelRef`.
 * Call `setTrack(null)` / `dispose()` to release the AudioContext.
 */
export function createLiveKitAdapter(): LiveKitLevelAdapter {
  const levelRef: { current: number } = { current: -1 }
  let ctx: AudioContext | null = null
  let analyser: AnalyserNode | null = null
  let source: MediaStreamAudioSourceNode | null = null
  let raf = 0
  let smoothed = 0
  let activeTrackId: string | null = null

  const halt = () => {
    if (raf !== 0) {
      cancelAnimationFrame(raf)
      raf = 0
    }
  }

  const teardownGraph = () => {
    halt()
    try {
      source?.disconnect()
    } catch {
      // ignore
    }
    source = null
    analyser = null
    if (ctx) {
      void ctx.close().catch(() => {})
      ctx = null
    }
    activeTrackId = null
    smoothed = 0
    levelRef.current = -1
  }

  const tick = () => {
    const node = analyser
    if (!node) {
      raf = 0
      return
    }
    const bins = node.frequencyBinCount
    const data = new Uint8Array(bins)
    node.getByteFrequencyData(data)
    const nyquist = node.context.sampleRate / 2
    const binFor = (hz: number) =>
      Math.min(bins, Math.max(1, Math.round((hz / nyquist) * bins)))
    const lo = binFor(VOICE_MIN_HZ)
    const hi = Math.max(lo + 1, binFor(VOICE_MAX_HZ))
    let sum = 0
    let peak = 0
    for (let i = lo; i < hi; i += 1) {
      const v = data[i] ?? 0
      sum += v
      if (v > peak) peak = v
    }
    const bandAvg = sum / (hi - lo) / 255
    const bandPeak = peak / 255
    const energy = (1 - PEAK_WEIGHT) * bandAvg + PEAK_WEIGHT * bandPeak
    const norm = Math.min(1, Math.max(0, (energy - LEVEL_FLOOR) / LEVEL_RANGE))
    smoothed += (norm - smoothed) * SMOOTHING
    levelRef.current = smoothed
    raf = requestAnimationFrame(tick)
  }

  const setTrack = (track: LiveKitTrackLike) => {
    const media = mediaTrackOf(track)
    if (!media) {
      teardownGraph()
      return
    }
    if (media.id === activeTrackId && analyser) return

    teardownGraph()
    activeTrackId = media.id

    try {
      const audioCtx = new AudioContext()
      if (audioCtx.state === 'suspended') void audioCtx.resume()
      const stream = new MediaStream([media])
      const src = audioCtx.createMediaStreamSource(stream)
      const anal = audioCtx.createAnalyser()
      anal.fftSize = 512
      anal.smoothingTimeConstant = 0.7
      src.connect(anal)
      ctx = audioCtx
      source = src
      analyser = anal
      raf = requestAnimationFrame(tick)
    } catch {
      teardownGraph()
    }
  }

  return {
    levelRef,
    setTrack,
    dispose: teardownGraph
  }
}
